import type { CSSProperties } from 'react';

import type { Theme } from '@mui/material/styles';
import type { CSSObject } from 'tss-react';

import { previewListStyles } from './preview-list-styles';

/**
 * Shared styles for previews of AI-generated content — the drafter card, the
 * redline insert/remove cards, and the redline visual diff. Compose them with
 * spread inside `makeStyles` and keep only genuinely local tweaks inline.
 * List-grid styles (markers, indentation) live in `preview-list-styles.ts`,
 * and the HTML itself must be rendered through `HtmlPreview`.
 */

/**
 * The bordered white card that preview content sits in: body2 text on a white
 * background with a hairline border.
 */
export function previewCardStyles(theme: Theme): CSSObject {
  return {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 4,
    fontSize: theme.typography.body2.fontSize,
    lineHeight: 1.6,
    padding: theme.spacing(1),
    wordBreak: 'break-word',
  };
}

/**
 * Tables inside an HTML preview: collapsed, full-width, with bordered cells.
 *
 * `cellBackground` tints the cells (the insert/remove cards pass their suggest
 * tint). `cellPadding` and `tableMarginTop` are in theme spacing units;
 * `tableMarginTop` lives here rather than at the call site because a later
 * `'& table'` key would replace this one's whole object, not merge with it.
 */
export function previewTableStyles(
  theme: Theme,
  options: { cellBackground?: string; cellPadding?: number; tableMarginTop?: number } = {}
): Record<string, CSSObject> {
  const { cellBackground, cellPadding = 0.5, tableMarginTop } = options;
  return {
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      ...(tableMarginTop === undefined ? {} : { marginTop: theme.spacing(tableMarginTop) }),
    },
    '& td, & th': {
      ...(cellBackground ? { backgroundColor: cellBackground } : {}),
      border: `1px solid ${theme.appColors.rule}`,
      padding: theme.spacing(cellPadding),
      verticalAlign: 'top',
    },
  };
}

/**
 * The suggested-edit text treatment: inserts read as blue underlined text,
 * removals as red struck-through text, matching the Aline Editor's rendering.
 * Returns `CSSProperties` so it can be spread into `makeStyles` classes and
 * into inline `style` objects alike.
 */
export function suggestTextStyles(
  appColors: Theme['appColors'],
  variant: 'insert' | 'remove'
): CSSProperties {
  const color = variant === 'insert' ? appColors.suggestInsert : appColors.suggestRemove;
  return {
    color,
    textDecorationColor: color,
    textDecorationLine: variant === 'insert' ? 'underline' : 'line-through',
  };
}

/**
 * The complete styling for a redline card's `HtmlPreview` content: the
 * bordered card, the list-marker grid, tinted tables, and the suggest text
 * treatment, with each block's tint hugging its text (via `fit-content`)
 * rather than filling the whole card. The insert and remove cards are
 * intentionally mirror images — same shapes, opposite palettes.
 */
export function suggestHtmlPreviewStyles(theme: Theme, variant: 'insert' | 'remove'): CSSObject {
  const isInsert = variant === 'insert';
  const contentBackground = isInsert
    ? theme.appColors.suggestInsertBackground
    : theme.appColors.suggestRemoveBackground;
  const cellBackground = isInsert
    ? theme.appColors.suggestInsertTableBackground
    : theme.appColors.suggestRemoveTableBackground;

  return {
    ...previewCardStyles(theme),
    ...previewListStyles(theme, { contentBackground, markerColumnWidth: 'auto' }),
    ...previewTableStyles(theme, { cellBackground, tableMarginTop: 0.5 }),
    ...suggestTextStyles(theme.appColors, variant),
    '& h1, & h2, & h3, & h4, & h5, & h6, & li, & p': {
      backgroundColor: contentBackground,
      margin: 0,
      width: 'fit-content',
    },
    '& p + p': {
      marginTop: theme.spacing(0.5),
    },
  };
}
